import { Plugins } from "@capacitor/core";
import {
	type ActionPerformed as LocalNotificationActionPerformed,
	LocalNotifications,
} from "@capacitor/local-notifications";
import {
	type ActionPerformed as PushNotificationActionPerformed,
	PushNotifications,
} from "@capacitor/push-notifications";
import * as Sentry from "@sentry/browser";

import { getInfo } from "~/lib/device";
import { DebugLogger } from "~/lib/log";
import { mobileVersion, type Version, versionLt } from "~/lib/version";

import {
	commitNewToken,
	DeviceContext,
	getCollapseKey,
	type NotificationPermissionState,
	NotificationsImpl,
	openNotificationURL,
} from "./shared";

const { FellowPlugin } = Plugins;
const log = new DebugLogger("[Notifications.android]").log;

export default class AndroidImpl extends NotificationsImpl {
	public readonly platform = DeviceContext.ANDROID;

	addEventListeners(): Promise<void> {
		PushNotifications.addListener("registrationError", error => {
			console.error("[Notifications.android] Registration error", { error });
			Sentry.captureException(error);
		});

		// FIXME: error handling for async function
		PushNotifications.addListener("pushNotificationReceived", async payload => {
			log("Received notification", { payload });
			const collapseKey = getCollapseKey(payload);
			if (collapseKey && this.idIsCleared(collapseKey)) {
				log("Key matches outerObjectContextId, not sending notification");
				return;
			}

			const extra = {
				url: payload.data.url,
				// eslint-disable-next-line camelcase
				collapse_key: collapseKey,
			};

			const { subtitle } = payload.data;
			const body = payload.body ?? payload.data.body;

			const notifications = [
				{
					// always a 5 digit number
					id: ((Math.random() * (99999 - 10000) + 1) | 0) + 10000,
					title: payload.title ?? payload.data.title,
					body: subtitle || body,
					largeBody: subtitle ? body : undefined,
					// @ts-ignore
					group: collapseKey,
					smallIcon: "ic_notification",
					extra,
				},
			];

			if (typeof collapseKey === "string") {
				const deliveredNotifications = (await PushNotifications.getDeliveredNotifications()).notifications;

				// only add the group summary notification if there isn't one already.
				// Capacitor dismisses any existing notifications with the same ID, and
				// dismissing the group summary dismisses the whole group, so this would
				// dismiss any notifications already in the group if there's already a
				// group summary
				if (
					// @ts-ignore
					!deliveredNotifications.some(({ group, groupSummary }) => group === collapseKey && groupSummary)
				) {
					log("No summary notification yet, adding one", { collapseKey });
					// add a group summary notification so they actually group together
					notifications.push({
						// use the string's hash code (according to Java, since that's what we
						// use in the NotificationService)
						id: (await FellowPlugin.stringHashCode({ string: collapseKey })).result,
						title: "Fellow",
						// @ts-ignore
						groupSummary: true,
						group: collapseKey,
						smallIcon: "ic_notification",
						extra,
					});
				}
			}

			LocalNotifications.schedule({ notifications });
		});

		function notificationHandler(payload: PushNotificationActionPerformed | LocalNotificationActionPerformed) {
			log("Notification was clicked", { payload });
			let data;
			if ("extra" in payload.notification) {
				data = payload.notification.extra;
			} else if ("data" in payload.notification) {
				data = payload.notification.data;
			}

			const url = data.url;
			if (typeof url === "string") {
				openNotificationURL(url);
			}
		}

		async function onToken({ value: token }: { value: string }) {
			const { id, platform } = await getInfo();
			commitNewToken(id, platform, token);
		}

		PushNotifications.addListener("registration", onToken);

		PushNotifications.addListener("pushNotificationActionPerformed", notificationHandler);
		LocalNotifications.addListener("localNotificationActionPerformed", notificationHandler);

		return Promise.resolve();
	}

	async requestPermission() {}

	protected async doRegister(): Promise<void> {
		// On older versions of the app, we don't have the FCM plugin
		const version: Version = await mobileVersion().catch(() => [0, 0, 0]);
		if (versionLt(version, [1, 1, 0])) {
			console.warn("[Notifications.android] Current app version is out of date:", version.join("."));
			return;
		}

		await PushNotifications.register().catch((e: string) => {
			console.error("[Notifications.android] Failed to get token", e);
			Sentry.captureException(e);
		});

		// FCM plugin has temporarily broken this code. Hopefully we can return to it eventually as it's simpler than the above
		// await PushNotifications.register()
		// 	.then(() => FCM.getToken())
		// 	.then(({ token }: { token: string }) => commitNewToken(deviceId, platform, token))
		// 	.catch((e: string) => {
		// 		console.error("[Notifications.android] Failed to get or commit token", e);
		// 		Sentry.captureException(e);
		// 	});
	}

	notificationPermission(): Promise<NotificationPermissionState> {
		return LocalNotifications.areEnabled().then(r => (r.value ? "granted" : "denied"));
	}

	async clearNotificationThread(id: string): Promise<void> {
		// These capacitor methods are available on Android as of 1.1.0
		const version: Version = await mobileVersion().catch(() => [0, 0, 0]);
		if (versionLt(version, [1, 1, 0])) {
			console.warn("[Notifications.android] Current app version is out of date:", version.join("."));
			return;
		}

		const deliveredNotifications = await PushNotifications.getDeliveredNotifications();
		const notifications = deliveredNotifications.notifications.filter(notif => getCollapseKey(notif) === id);

		if (notifications.length > 0) {
			log("Found existing notifications in thread, removing them");
			PushNotifications.removeDeliveredNotifications({ notifications });
		}
	}
}
